import React, { useEffect, useMemo, useState } from 'react'
import '../../login.css'
import axios from 'axios'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import tmsLogoDark from '../../img/TMS LOGO DARK.svg'
import shypvLogoWhite from '../../img/Shypv Logo SVG White.svg'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get('token') || '', [searchParams])
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgType, setMsgType] = useState('') // 'error' | 'success'
  const [isLoading, setIsLoading] = useState(false)
  const [done, setDone] = useState(false)

  const API_URL = process.env.REACT_APP_API_URL
  const APP_VERSION = process.env.REACT_APP_VERSION || '1.1.0'
  const API_PATH = `${API_URL}shypvdriverapp/personnel/reset-password`

  const features = [
    { icon: 'fa-lock', title: 'Choose a Strong Password', description: 'Use at least 8 characters with a mix of letters, numbers, and symbols.' },
    { icon: 'fa-shield-halved', title: 'One-time Link', description: 'This reset link is valid only once and expires after 30 minutes.' },
    { icon: 'fa-circle-check', title: 'Almost Done', description: 'After resetting, sign in with your new password to continue.' },
  ]
  const [activeFeature, setActiveFeature] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length)
    }, 3500)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function submit(e) {
    if (e) e.preventDefault()
    setMsg('')
    setMsgType('')

    if (!token) {
      setMsg('Reset link is missing or invalid. Please request a new one.')
      setMsgType('error')
      return
    }
    if (password.length < 8) {
      setMsg('Password must be at least 8 characters.')
      setMsgType('error')
      return
    }
    if (password !== confirmPassword) {
      setMsg('Passwords do not match.')
      setMsgType('error')
      return
    }

    setIsLoading(true)
    axios({
      method: 'post',
      url: API_PATH,
      data: { token, password },
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (res?.data?.success === true) {
          setMsg(res?.data?.message || 'Password reset successful. You can now sign in.')
          setMsgType('success')
          setDone(true)
          setTimeout(() => navigate('/login'), 2200)
        } else {
          const errorMessage =
            res?.data?.errors?.[0]?.errorMessage ||
            res?.data?.message ||
            'Reset link is invalid or has expired. Please request a new one.'
          setMsg(errorMessage)
          setMsgType('error')
        }
        setIsLoading(false)
      })
      .catch(() => {
        setMsg('Something went wrong while resetting your password. Please try again.')
        setMsgType('error')
        setIsLoading(false)
      })
  }

  const formStyles = {
    iconWrapper: {
      width: '64px', height: '64px', backgroundColor: '#f3f4f6', borderRadius: '16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
    icon: { fontSize: '24px', color: '#1f2937' },
    title: { fontSize: '24px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0', textAlign: 'center' },
    subtitle: { fontSize: '14px', color: '#6b7280', margin: '0 0 32px 0', lineHeight: '1.5', textAlign: 'center' },
    inputGroup: { position: 'relative', marginBottom: '16px' },
    inputIcon: {
      position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
      color: '#9ca3af', fontSize: '16px', pointerEvents: 'none',
    },
    input: {
      width: '100%', padding: '16px 16px 16px 48px', fontSize: '15px', border: 'none',
      borderRadius: '14px', backgroundColor: '#f3f4f6', color: '#111827', outline: 'none',
      boxSizing: 'border-box', transition: 'all 0.2s ease',
    },
    eyeButton: {
      position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
      padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    submitButton: {
      width: '100%', padding: '16px 24px', fontSize: '16px', fontWeight: '600', color: '#ffffff',
      backgroundColor: '#1f2937', border: 'none', borderRadius: '14px', cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
  }

  return (
    <div className="login-page">
      <title>Reset Password - Shypv Dashboard</title>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/bootstrap/3.4.1/css/bootstrap.min.css" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      <link rel="stylesheet" href="https://www.w3schools.com/w3css/4/w3.css" />
      <style>{`
        .new-form-input::placeholder { color: #9ca3af; }
        .new-form-input:focus { background-color: #ffffff !important; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1); }
        .login-btn { transition: all 0.3s ease !important; }
        .login-btn:hover:not(:disabled) { background-color: #374151 !important; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31, 41, 55, 0.3); }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        .login-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .login-btn .spinner { display: inline-block; width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-radius: 50%; border-top-color: #fff; animation: spin 0.8s ease-in-out infinite; margin-right: 8px; vertical-align: middle; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-right-panel { flex: 1; background: #ffffff; display: flex; align-items: center; justify-content: center; padding: 40px; min-height: 100%; }
        .form-wrapper { display: flex; flex-direction: column; align-items: center; gap: 32px; width: 100%; max-width: 380px; }
        .logo-above-form { height: 40px; width: auto; }
        .info-card { background-color: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 12px; padding: 12px 16px; margin-top: 16px; display: flex; align-items: center; gap: 10px; }
        .info-card i { color: #059669; font-size: 16px; }
        .info-card span { color: #065f46; font-size: 13px; font-weight: 500; }
        .error-card { background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; padding: 12px 16px; margin-top: 16px; display: flex; align-items: center; gap: 10px; animation: shakeError 0.4s ease-in-out; }
        .error-card i { color: #dc2626; font-size: 16px; }
        .error-card span { color: #b91c1c; font-size: 13px; font-weight: 500; }
        @keyframes shakeError { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
      `}</style>
      <div className="login-shell">
        <div className="login-visual">
          <div className="login-visual-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <span className="login-eyebrow" style={{ fontWeight: '700', fontSize: '16px', letterSpacing: '2px' }}>RESET PASSWORD</span>
              <div className="login-feature" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="login-feature-icon" style={{ marginBottom: '16px' }}>
                  <i className={`fa ${features[activeFeature].icon}`} aria-hidden="true" />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{ textAlign: 'center' }}>{features[activeFeature].title}</h2>
                  <p style={{ textAlign: 'center' }}>{features[activeFeature].description}</p>
                </div>
              </div>
              <div className="login-progress">
                {features.map((_, index) => (
                  <span key={index} className={index === activeFeature ? 'login-dot active' : 'login-dot'} />
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'center', paddingBottom: '30px' }}>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '12px' }}>
                Version <strong style={{ fontWeight: '700' }}>{APP_VERSION}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Powered by</span>
                <img src={shypvLogoWhite} alt="Shypv" style={{ height: '20px', width: 'auto' }} />
              </div>
            </div>
          </div>
        </div>

        <div className="login-right-panel">
          <div className="form-wrapper">
            <img src={tmsLogoDark} alt="TMS" className="logo-above-form" style={{ height: '50px', width: 'auto' }} />

            <div style={formStyles.iconWrapper}>
              <i className="fa-solid fa-lock-open" style={formStyles.icon}></i>
            </div>

            <div style={{ textAlign: 'center', marginTop: '-16px' }}>
              <h1 style={formStyles.title}>Set a new password</h1>
              <p style={formStyles.subtitle}>
                Enter a new password for your account.<br />
                Make sure it's at least 8 characters.
              </p>
            </div>

            <form onSubmit={submit} style={{ width: '100%' }}>
              <div style={formStyles.inputGroup}>
                <i className="fa-solid fa-lock" style={formStyles.inputIcon}></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New password"
                  className="new-form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...formStyles.input, paddingRight: '44px' }}
                  required
                  disabled={done}
                />
                <button type="button" style={formStyles.eyeButton} onClick={() => setShowPassword(!showPassword)}>
                  <i className={showPassword ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'} style={{ fontSize: '15px' }}></i>
                </button>
              </div>

              <div style={formStyles.inputGroup}>
                <i className="fa-solid fa-lock" style={formStyles.inputIcon}></i>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  className="new-form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ ...formStyles.input, paddingRight: '44px' }}
                  required
                  disabled={done}
                />
                <button type="button" style={formStyles.eyeButton} onClick={() => setShowConfirm(!showConfirm)}>
                  <i className={showConfirm ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'} style={{ fontSize: '15px' }}></i>
                </button>
              </div>

              <button type="submit" className="login-btn" style={formStyles.submitButton} disabled={isLoading || done}>
                {isLoading ? (<><span className="spinner"></span>Resetting...</>) : (done ? 'Password updated' : 'Reset password')}
              </button>

              {msg && msgType === 'success' && (
                <div className="info-card">
                  <i className="fa-solid fa-circle-check"></i>
                  <span>{msg}</span>
                </div>
              )}
              {msg && msgType === 'error' && (
                <div className="error-card">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <span>{msg}</span>
                </div>
              )}
            </form>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                <Link to="/login" style={{ color: '#4f46e5', fontWeight: '600', textDecoration: 'none' }}>
                  Back to login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
